import { motion, useIsPresent } from "motion/react";
import { type ReactNode, useLayoutEffect, useRef } from "react";
import { FOOTER_SLOT_OFFSET_Y, FOOTER_SLOT_TIMING, getRevealProps } from "../../lib/motion-presets";

export type FooterSlotProps = {
  children: ReactNode;
  motionEnabled: boolean;
  /** Called once this slot has finished arriving, never when it is leaving. */
  onSettled?: () => void;
};

/** Long enough to open smoothly, short enough to be done before the spring overshoots. */
const HEIGHT_DURATION_S = 0.24;

/**
 * How long a slot takes to clear out. The fold is the slower of the exit's two halves —
 * the inner slide is quicker — so it is what anything waiting on the departure waits for.
 */
export const FOOTER_SLOT_EXIT_DURATION_S = HEIGHT_DURATION_S;

/**
 * One half of the footer's exclusive slot — the input, or a form — as it rises into
 * place and later slides back down.
 *
 * Two nested elements, because the two halves of the movement have to finish at
 * different times:
 *
 * - the OUTER one folds the slot's height. The input and a form are very different
 *   sizes, so without this the footer would snap from one to the other the instant the
 *   swap happens. Letting each half take its own space with it keeps the height moving
 *   continuously. It is a tween, never the spring: a spring would overshoot past the
 *   element's real size and show a strip of nothing under it. It deliberately does NOT
 *   clip: the point of the slide is to watch the slot cross over the footer's branding,
 *   and a box growing from zero around it would hide exactly that, turning the entrance
 *   into a reveal while the exit — which is never mid-fold — still slid freely.
 * - the INNER one slides and fades, and keeps springing after the height has settled.
 *
 * A slot on its way out is still in the document for the length of its exit, and by
 * default still focused and still interactive: the viewer could keep typing into an input
 * that is visibly sliding away, and on mobile the keyboard would stay up over the form
 * arriving behind it. Releasing focus and going `inert` the moment it stops being present
 * makes the exit purely visual.
 */
export const FooterSlot = ({ children, motionEnabled, onSettled }: FooterSlotProps) => {
  const isPresent = useIsPresent();
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (isPresent) {
      return;
    }

    const node = ref.current;
    const active = document.activeElement;

    if (node && active instanceof HTMLElement && node.contains(active)) {
      active.blur();
    }
  }, [isPresent]);

  const height = motionEnabled
    ? {
        initial: { height: 0 },
        animate: { height: "auto" as const },
        exit: { height: 0 },
        transition: { duration: HEIGHT_DURATION_S, ease: "easeOut" as const },
      }
    : {};

  return (
    <motion.div {...height} inert={!isPresent || undefined}>
      <motion.div
        // The input carries `relative z-2` to sit above the footer gradient. Animating
        // writes a transform here, and a transform opens a new stacking context, so the
        // z-index has to live on this element or the content slides under the gradient.
        className="relative z-2"
        onAnimationComplete={() => {
          if (isPresent) {
            onSettled?.();
          }
        }}
        ref={ref}
        {...getRevealProps({
          motionEnabled,
          timing: FOOTER_SLOT_TIMING,
          offsetY: FOOTER_SLOT_OFFSET_Y,
          withExit: true,
        })}
      >
        {children}
      </motion.div>
    </motion.div>
  );
};
